import { Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Shield, Smartphone } from 'lucide-react'

interface Props {
  user: {
    id: number
    name: string
    phone: string
    email: string | null
    is_superadmin: boolean
    created_at: string
  }
  organizations: Array<{
    id: number
    name: string
    slug: string
    role: string
  }>
  deviceTokens: Array<{
    id: number
    platform: string
    created_at: string
  }>
}

export default function Show({ user, organizations, deviceTokens }: Props) {
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/admin/users">
              <ArrowLeft className="mr-1 h-4 w-4" /> Volver
            </Link>
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">{user.name}</h1>
              {user.is_superadmin && (
                <Badge variant="default" className="gap-1">
                  <Shield className="h-3 w-3" /> Superadmin
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {user.phone} {user.email && `· ${user.email}`} · Registrado {new Date(user.created_at).toLocaleDateString('es-MX')}
            </p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Organizations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Organizaciones ({organizations.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organización</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Rol</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                        Sin organizaciones
                      </TableCell>
                    </TableRow>
                  ) : (
                    organizations.map((org) => (
                      <TableRow key={org.id}>
                        <TableCell>
                          <Link href={`/admin/organizations/${org.id}`} className="font-medium hover:underline">
                            {org.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{org.slug}</TableCell>
                        <TableCell>
                          <Badge variant={org.role === 'owner' ? 'default' : 'secondary'}>{org.role}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Device Tokens */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Dispositivos ({deviceTokens.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plataforma</TableHead>
                    <TableHead>Registrado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deviceTokens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">
                        Sin dispositivos registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    deviceTokens.map((dt) => (
                      <TableRow key={dt.id}>
                        <TableCell>
                          <Badge variant="outline">{dt.platform}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {new Date(dt.created_at).toLocaleDateString('es-MX')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
